import { formatPresenceReturn } from "@talenthop/shared";
import type { IntegrityEventKind } from "@talenthop/shared";

/**
 * Candidate · camera-presence detection. Answers one narrow question, once a second: how many
 * faces are in the frame right now — 0, 1, or more. Nothing else. It never identifies anyone,
 * never measures expression, emotion, attractiveness, age or "engagement", and nothing leaves the
 * device: no frame, no crop, no bounding box, no embedding. The interview's speech stack is 100%
 * client-side and costs us nothing per interview; vision is held to exactly the same rule, so
 * there is no cloud vision call here and none may be added.
 *
 * TWO ENGINES, BOTH LOCAL:
 *
 *   1. `FaceDetector`, the browser's own Shape Detection API — free, instant, zero bytes. It is
 *      also, honestly, barely shipped: Chromium hides it behind experimental-web-platform-features
 *      on desktop, Firefox and Safari never implemented it. Where it exists we use it and stop.
 *      (This module used to BE just this branch, which is why the product claimed camera presence
 *      it almost never actually had.)
 *
 *   2. Otherwise the self-hosted MediaPipe BlazeFace model in faceDetectionModel.ts, behind a
 *      dynamic import. That is what makes presence real on a normal candidate's browser — and it
 *      is ~11 MB of wasm, so `shouldLoadLocalModel` below decides whether asking this particular
 *      candidate to download it is a decent thing to do.
 *
 * Neither available → `createFaceSource` resolves null, the caller emits nothing and shows
 * nothing, and the report says camera presence was not observed. No detector means NO DATA, never
 * a fabricated "all clear": a candidate must not be scored as watched-and-clean when nobody was
 * watching, and must not be penalised for their browser either.
 *
 * TWO GATES, both of which have been wrong before:
 *   • NO CAMERA, NO DETECTOR. useProctoring must not call `createFaceSource` at all unless the
 *     camera is genuinely live. A detector that can never read a frame is 11.5 MB spent to learn
 *     nothing — and, before `guardedFaceRead`, it was 11.5 MB spent to accuse someone.
 *   • NO FRAME, NO READING. Every engine reads through `guardedFaceRead`, so an unreadable video
 *     yields null and null leaves the machine untouched.
 */

// ── the detector seam ─────────────────────────────────────────────────────────

export type FaceEngine = "shapedetection" | "mediapipe";

export interface FaceSource {
  readonly engine: FaceEngine;
  /**
   * Faces currently in frame, or null when this tick produced NO READING — the video has no
   * decoded frame yet, the camera is off or denied, or the detector hiccuped. null is emphatically
   * not "zero faces": a candidate whose camera is off must never be flagged as having left frame.
   */
  read(video: HTMLVideoElement): Promise<number | null>;
  close(): void;
}

/** Shape Detection API, as much of it as we use. Absent from lib.dom — it is experimental. */
interface ShapeDetectorLike {
  detect(source: CanvasImageSource): Promise<unknown[]>;
}
type ShapeDetectorCtor = new (opts?: { fastMode?: boolean; maxDetectedFaces?: number }) => ShapeDetectorLike;

/**
 * A frame we can actually read: decoded and sized. An absent camera never gets past this.
 *
 * EXPORTED, and it must stay that way. This guard was once private to this file and applied to the
 * native branch alone — the branch almost nobody runs. The MediaPipe branch, which is what a real
 * candidate actually uses, went straight to the detector, and MediaPipe answers an undecoded
 * <video> with a [0,0] size probe and zero detections rather than an exception, so a candidate who
 * had DENIED the camera was read as "0 faces" once a second and accused of hiding from a camera
 * that was never on. Every engine reads through `guardedFaceRead` below now.
 */
export function frameReadable(video: HTMLVideoElement): boolean {
  return video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0;
}

/**
 * The ONE way an engine is allowed to produce a reading. Both branches go through it, so the
 * blank-frame guard cannot be reintroduced on one path and forgotten on the other.
 *
 * Three things collapse to null — NO READING — and none of them may ever become the number 0:
 *   • the frame is not readable (camera off, denied, or not yet decoded). The detector is not even
 *     asked, because asking is exactly how the accusation was manufactured.
 *   • the detector threw. It also might NOT throw where you would expect: WebGL's texImage2D on a
 *     HAVE_NOTHING video raises a GL error, not a JS one, so `catch` alone was never a guard.
 *   • the detector answered with something that is not a finite, non-negative count.
 */
export function guardedFaceRead(
  video: HTMLVideoElement,
  detect: (video: HTMLVideoElement) => number | null | undefined,
): number | null {
  if (!frameReadable(video)) return null;
  let count: number | null | undefined;
  try {
    count = detect(video);
  } catch {
    return null;
  }
  return typeof count === "number" && Number.isFinite(count) && count >= 0 ? count : null;
}

function createShapeDetectionSource(): FaceSource | null {
  if (typeof window === "undefined") return null;
  const Ctor = (window as unknown as { FaceDetector?: ShapeDetectorCtor }).FaceDetector;
  if (!Ctor) return null;
  let detector: ShapeDetectorLike;
  try {
    detector = new Ctor({ fastMode: true, maxDetectedFaces: 5 });
  } catch {
    return null; // global present but unusable (flag half-on, origin trial expired)
  }
  return {
    engine: "shapedetection",
    async read(video) {
      if (!frameReadable(video)) return null;
      // `detect` is async, so this branch cannot use guardedFaceRead's sync callback — the frame
      // check above is the same one, deliberately duplicated rather than skipped.
      try {
        const found = await detector.detect(video);
        return Array.isArray(found) ? found.length : null;
      } catch {
        return null;
      }
    },
    close() {
      /* nothing to release */
    },
  };
}

/** What we know about the price this candidate would pay for the download. */
export interface DownloadCost {
  /** navigator.connection.saveData — the candidate has asked every site to use less data. */
  saveData?: boolean;
  /** navigator.connection.effectiveType. Undefined means the browser would not say. */
  effectiveType?: string;
  /** RAM in GB, where the browser reports it (navigator.deviceMemory, Chromium-only). */
  deviceMemory?: number;
  /** This looks like a phone or tablet — a mobile UA, or a touch-first pointer. */
  handheld?: boolean;
}

/** A 2 GB device is already running speech recognition, a recorder and a video element. */
const MIN_DEVICE_MEMORY_GB = 3;

/**
 * Should we spend ~11.5 MB of this candidate's connection and heap on presence detection?
 *
 * Presence is an ADVISORY signal for the recruiter. It is not worth burning a candidate's mobile
 * data allowance, and it is certainly not worth competing with the interview's own audio on a 2G
 * link. When the download looks expensive we skip the model entirely and report honestly that
 * camera presence was not observed — a missing advisory line is a far smaller harm than a
 * candidate whose interview stutters because we were downloading a neural network.
 *
 * UNKNOWN IS NOT PERMISSION. `navigator.connection` is Chromium-only: Safari and Firefox return
 * undefined, so a rule that refused only on a self-declared 2G link said "yes, download it" to
 * every iPhone in the world. Where the connection is unknown we fall back to what the device
 * itself tells us, and a handheld with nothing to say gets the benefit of the doubt in the
 * CANDIDATE'S favour, not ours.
 *
 * `VITE_FACE_PRESENCE=off` is the kill switch: it disables the heavy model everywhere without a
 * code change. The free native detector, where a browser has one, keeps working either way.
 */
export function shouldLoadLocalModel(env: Record<string, unknown>, cost: DownloadCost | undefined): boolean {
  if (String(env.VITE_FACE_PRESENCE ?? "").toLowerCase() === "off") return false;
  if (cost?.saveData === true) return false;
  if (typeof cost?.deviceMemory === "number" && cost.deviceMemory < MIN_DEVICE_MEMORY_GB) return false;
  const type = cost?.effectiveType;
  if (type === "slow-2g" || type === "2g") return false;
  // No usable connection reading at all → don't gamble a phone's data plan on an advisory line.
  if (type === undefined && cost?.handheld === true) return false;
  return true;
}

/** True for a phone or tablet, by the cheapest reliable signal available. */
function isHandheld(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
    maxTouchPoints?: number;
  };
  if (typeof nav.userAgentData?.mobile === "boolean") return nav.userAgentData.mobile;
  const ua = typeof nav.userAgent === "string" ? nav.userAgent : "";
  if (/Android|iPhone|iPad|iPod|Mobile|Tablet|Silk|Kindle/i.test(ua)) return true;
  // iPadOS 13+ claims to be a Macintosh; a "desktop" with a touchscreen this good is a tablet.
  if (/Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1) return true;
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    try {
      return window.matchMedia("(pointer: coarse)").matches;
    } catch {
      return false;
    }
  }
  return false;
}

/** Exported for gazeDetection.ts, whose strict-mode landmarker pays the same download bill and
 *  must answer the same "is this decent to ask of this candidate's connection?" question. */
export function downloadCost(): DownloadCost {
  if (typeof navigator === "undefined") return {};
  const nav = navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
    deviceMemory?: number;
  };
  return {
    saveData: nav.connection?.saveData,
    effectiveType: nav.connection?.effectiveType,
    deviceMemory: nav.deviceMemory,
    handheld: isHandheld(),
  };
}

/**
 * Pick the best available local engine, or null. Never throws and never blocks: every failure path
 * has to end with the interview running exactly as it would have with no camera at all.
 *
 * CALL THIS ONLY WHEN THE CAMERA IS LIVE. It is the caller's job (useProctoring) not to reach here
 * for a candidate who has no camera: a detector that can never read a frame is 11.5 MB spent on
 * nothing, and this function has no way to know.
 */
export async function createFaceSource(
  env: Record<string, unknown> = (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {},
  cost: DownloadCost = downloadCost(),
): Promise<FaceSource | null> {
  const native = createShapeDetectionSource();
  if (native) return native;
  if (typeof window === "undefined") return null; // SSR / unit tests: never reach for the model
  if (!shouldLoadLocalModel(env, cost)) return null;
  try {
    const { createMediaPipeFaceSource } = await import("./faceDetectionModel.js");
    return await createMediaPipeFaceSource();
  } catch {
    return null; // chunk failed to load (offline, blocked) — no data, no claim
  }
}

// ── the debounce machine (pure) ───────────────────────────────────────────────

export type PresenceStatus = "ok" | "absent" | "multiple";

/** One integrity event the machine wants emitted, in the shared vocabulary. */
export interface PresenceEmit {
  kind: Extract<IntegrityEventKind, "no_face" | "second_face" | "face_visible">;
  detail?: string;
}

export interface PresenceMachine {
  /** What we have CONFIRMED and reported. Starts optimistic: nobody is absent until proven so. */
  confirmed: PresenceStatus;
  /** What the recent readings suggest, not yet confirmed. */
  pending: PresenceStatus;
  /** When the current pending run started, and how many readings back it. */
  pendingSince: number;
  pendingSamples: number;
  /** When `confirmed` last changed — the clock behind the duration on the return event. */
  confirmedSince: number;
  /** When we last got a real reading. 0 before the first one. Guards against a run being
   *  stitched together across a long blind gap — see READING_GAP_RESET_MS. */
  lastReadingAt: number;
}

/**
 * How long a state must HOLD before we believe it. These numbers decide whether a real person gets
 * flagged for being human, so they are deliberately generous:
 *
 *   • Looking down at notes, rubbing your eyes, a hand crossing the lens, turning to cough, a
 *     toddler wandering past — all of that is over in a second or two and MUST NOT produce a flag.
 *   • A detector missing a face on one frame (low light, a turned head) must not either, which is
 *     why we require consecutive samples as well as elapsed time.
 *   • Coming back clears faster than leaving fires: the notice should vanish the moment the
 *     candidate fixes it, and never nag.
 */
export const FACE_SAMPLE_MS = 1_000; // 1 Hz — plenty, and cheap beside speech + recording
export const CONFIRM_MS: Record<PresenceStatus, number> = { absent: 5_000, multiple: 4_000, ok: 1_500 };
export const CONFIRM_SAMPLES: Record<PresenceStatus, number> = { absent: 4, multiple: 3, ok: 2 };

/**
 * How long a blind spell may last before the run in progress is abandoned. Two absent readings,
 * then a stalled video for half a minute, then two more absent readings is NOT "absent for 35
 * seconds" — we were not looking for most of it, and stitching those four samples into one
 * confirmed absence would flag a candidate on evidence we do not have.
 */
export const READING_GAP_RESET_MS = 3 * FACE_SAMPLE_MS;

export function createPresenceMachine(now = 0): PresenceMachine {
  return {
    confirmed: "ok",
    pending: "ok",
    pendingSince: now,
    pendingSamples: 0,
    confirmedSince: now,
    lastReadingAt: 0,
  };
}

export function statusFor(faces: number): PresenceStatus {
  return faces <= 0 ? "absent" : faces >= 2 ? "multiple" : "ok";
}

/**
 * Feed one reading in, get the next machine and any events to emit.
 *
 * `faces === null` means NO READING (camera off, frame not decoded, detector hiccup). It leaves
 * the machine completely untouched — neither confirming nor resetting a run — so an unreadable
 * camera can never accumulate into "this candidate disappeared".
 */
export function observeFaces(
  machine: PresenceMachine,
  faces: number | null,
  now: number,
): { machine: PresenceMachine; emits: PresenceEmit[] } {
  if (faces === null) return { machine, emits: [] };

  const status = statusFor(faces);
  const blindGap = machine.lastReadingAt > 0 && now - machine.lastReadingAt > READING_GAP_RESET_MS;
  const next: PresenceMachine =
    status === machine.pending && !blindGap
      ? { ...machine, pendingSamples: machine.pendingSamples + 1, lastReadingAt: now }
      : { ...machine, pending: status, pendingSince: now, pendingSamples: 1, lastReadingAt: now };

  if (
    next.pending === next.confirmed ||
    now - next.pendingSince < CONFIRM_MS[next.pending] ||
    next.pendingSamples < CONFIRM_SAMPLES[next.pending]
  ) {
    return { machine: next, emits: [] };
  }

  // Confirmed transition. Any state we LEAVE that wasn't "ok" gets a receipt first, carrying how
  // long it lasted, so the report can say "out of frame for 24s" rather than "no_face ×1" — and so
  // a straight absent→multiple flip doesn't quietly lose the absence's duration. Then the new
  // state's own event, if it is one worth flagging.
  const from = next.confirmed;
  const heldMs = now - next.confirmedSince;
  const emits: PresenceEmit[] = [];
  if (from !== "ok") emits.push({ kind: "face_visible", detail: formatPresenceReturn(from, heldMs) });
  if (next.pending === "absent") emits.push({ kind: "no_face" });
  else if (next.pending === "multiple") emits.push({ kind: "second_face", detail: String(faces) });

  return { machine: { ...next, confirmed: next.pending, confirmedSince: now }, emits };
}

// ── what the candidate is told ────────────────────────────────────────────────

/**
 * The candidate-facing notice — the "alerting" half of this feature. Calm, specific, actionable,
 * and never an accusation: we say what we can see, what would help, and that nothing is being held
 * against them. It must never block, never pause the interview and never imply a penalty. A
 * candidate whose flatmate walked behind them should not spend the next question worrying.
 */
export interface CameraNotice {
  status: Exclude<PresenceStatus, "ok">;
  title: string;
  body: string;
}

export function noticeFor(status: PresenceStatus): CameraNotice | null {
  if (status === "absent") {
    return {
      status,
      title: "We can't see you on camera. Please make sure your face is visible.",
      body: "Your interview is still running — carry on answering. This is context for the recruiter, never a mark against you.",
    };
  }
  if (status === "multiple") {
    return {
      status,
      title: "It looks like more than one person is in view.",
      body: "Please carry on by yourself where you can. Nothing is paused, and nobody is judged on their surroundings.",
    };
  }
  return null;
}
